import React, { useState, useEffect } from 'react';
import { TextField, Box, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Typography, Divider, Button, Pagination, Stack } from '@mui/material';
import DataList from './DataList';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const TasksList = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [selectedTaskForActions, setSelectedTaskForActions] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'construction_title', label: 'Объект' },
    { key: 'status', label: 'Статус', render: (value) => {
      if (value === 'new') return 'Новая / На утверждении';
      if (value === 'agree') return 'Утверждена ✅';
      if (value === 'not_agree') return 'Не утверждена ❌';
      return value;
    } },
    { key: 'type_task', label: 'Тип', render: (value) => value === 'invoice' ? 'Счет' : value === 'task' ? 'Задача' : value },
    { key: 'author', label: 'Автор', render: (value, item) => `${item.author_telegram}\n${item.author_roles && item.author_roles.length > 0 ? item.author_roles.join(', ') : 'Нет ролей'}` },
    { key: 'script_info', label: 'Скрипт' },
    { key: 'on_user', label: 'Текущий сотрудник', render: (value, item) => item.on_user_telegram ? `${item.on_user_telegram}\n${item.on_user_roles && item.on_user_roles.length > 0 ? item.on_user_roles.join(', ') : 'Нет ролей'}` : 'Не назначен' },
    { key: 'date_create', label: 'Дата создания', render: (value) => new Date(value).toLocaleString('ru-RU') },
    { key: 'actions', label: 'Действия', render: (value, item) => <Button variant="outlined" onClick={(e) => { e.stopPropagation(); handleActionsClick(item); }}>Действия</Button> },
  ];

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/tasks/?page=${page}`);
      const fetchedData = response.data.results || response.data;
      setData(Array.isArray(fetchedData) ? fetchedData : []);
      // Получаем общее количество страниц из заголовков или из данных
      const count = response.data.count;
      if (count) {
        setTotalPages(Math.ceil(count / 20)); // PAGE_SIZE = 20 в Django
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const filteredData = data.filter(item =>
    item.id.toString().includes(searchTerm)
  );

  const handleRowClick = (task) => {
    setSelectedTask(task);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const handleActionsClick = (task) => {
    setSelectedTaskForActions(task);
    setActionsModalOpen(true);
  };

  const handleCloseActionsModal = () => {
    setActionsModalOpen(false);
    setSelectedTaskForActions(null);
  };

  const handleRemind = async () => {
    try {
      await api.post(`/api/tasks/${selectedTaskForActions.id}/remind/`);
      alert('Напоминание отправлено');
      handleCloseActionsModal();
    } catch (err) {
      alert('Ошибка отправки напоминания');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      try {
        await api.delete(`/api/tasks/${selectedTaskForActions.id}/`);
        setData(data.filter(task => task.id !== selectedTaskForActions.id));
        handleCloseActionsModal();
      } catch (err) {
        alert('Ошибка удаления задачи');
      }
    }
  };

  const handleDownloadFile = async (fileId) => {
    try {
      const response = await api.get(`/api/files/${fileId}/download/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = `file_${fileId}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const isAdmin = user && user.is_staff;

  const getStatusDisplay = (status) => {
    const statuses = { 'no': '❌', 'com': '👍', 'yes': '✅' };
    return statuses[status] || status;
  };

  if (loading) return <div>Загрузка...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <Box>
      <TextField
        label="Поиск по ID задачи"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: '16px' }}
      />
      <DataList data={filteredData} columns={columns} onRowClick={handleRowClick} />
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Детали задачи #{selectedTask?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="h6">Комментарии</Typography>
          {selectedTask?.comments && selectedTask.comments.length > 0 ? (
            <List>
              {selectedTask.comments.map((comment, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${new Date(comment.date_create).toLocaleString('ru-RU')}: [${getStatusDisplay(comment.status)}] ${comment.author_name}(${comment.role_name}) - ${comment.text}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Нет комментариев</Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Прикрепленные файлы</Typography>
          {selectedTask?.task_file && selectedTask.task_file.length > 0 ? (
            <List>
              {selectedTask.task_file.map((file, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`Тип: ${file.type_file}, Автор: ${file.author_telegram}(${file.author_role}), Дата: ${new Date(file.date_create).toLocaleString('ru-RU')}`}
                    secondary={isAdmin ? null : `ID файла: ${file.file_id}`}
                  />
                  {isAdmin && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleDownloadFile(file.id)}
                      style={{ marginLeft: '16px' }}
                    >
                      Скачать файл
                    </Button>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Нет прикрепленных файлов</Typography>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={actionsModalOpen} onClose={handleCloseActionsModal}>
        <DialogTitle>Действия для задачи #{selectedTaskForActions?.id}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleRemind}>
              Напомнить текущему сотруднику
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Удалить
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TasksList;